import { Alert, Box, Button, Checkbox, FormControlLabel, Paper, TextField, Typography } from '@mui/material';
import { useModal } from '@refinedev/core';
import { Edit } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { FieldValues, FormProvider, Controller } from 'react-hook-form';

import { FixedOptionSelector, Selector } from '../../components/inputs';
import { UnifiedMitigationContentEditor } from '../../components/inputs/UnifiedMitigationsContentEditor/UnifiedMitigationsContentEditor';
import TaggedItemsEditor from '../../components/TaggedItemsEditor';
import HistoryDrawer from '../scenarios/components/HistoryDrawer';

import { SEVERITY, SOURCE_TYPE, STAGE } from './constants';

const UnifiedMitigationsEdit = () => {
  const methods = useForm({
    refineCoreProps: {
      queryOptions: {
        gcTime: 0,
        staleTime: 0
      }
    }
  });

  const {
    refineCore: { formLoading, onFinish, query: queryResult },
    control,
    saveButtonProps,
    watch,
    formState: { errors }
  } = methods;

  const unifiedMitigationId = queryResult?.data?.data['id'] as string;

  const drawer = useModal();

  const onSubmit = (data: FieldValues) => {
    if (onFinish) onFinish(data);
  };

  return (
    <FormProvider {...methods}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper variant="outlined">
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2">Unified Mitigation ID: {queryResult?.data?.data['id']}</Typography>
          </Box>
          <Box sx={{ mx: 1.5 }}>
            <Button
              type="button"
              variant="contained"
              onClick={() => {
                drawer.show();
              }}
            >
              HISTORY
            </Button>
          </Box>
        </Paper>
        <form onSubmit={methods.handleSubmit(onSubmit)} autoComplete="off" style={{ width: '100%' }}>
          <Edit isLoading={formLoading} saveButtonProps={{ ...saveButtonProps, type: 'submit' }}>
            <Alert severity="info">
              You are editing a UNIFIED MITIGATION. Note that this operation will also edit the appropriate "Legacy"
              Mitigation, until we get to remove those.
            </Alert>
            <br />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Controller
                rules={{ required: 'Unified Mitigation name is required' }}
                name="name"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value || ''}
                    onChange={onChange}
                    error={!!errors.name}
                    helperText={errors.name?.message as string}
                    label="Mitigation Name"
                    autoFocus
                    fullWidth
                    required
                  />
                )}
              />

              <Selector
                resource="unified_mitigation_types"
                label="Unified Mitigation Type"
                control={control}
                name="unifiedmitigationtype"
                rules={{ required: 'Unified Mitigation type is required' }}
                disabled
              />
              <Typography variant="body1">Content*</Typography>
              <Controller
                name="content"
                control={control}
                rules={{ required: 'Unified Mitigation content is required' }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <UnifiedMitigationContentEditor
                    value={value}
                    onChange={onChange}
                    type={watch('unifiedmitigationtype')}
                    readonly={false}
                  />
                )}
              />

              <Controller
                name="reference_id"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value || ''}
                    onChange={onChange}
                    error={!!errors.reference_id}
                    helperText={errors.reference_id?.message as string}
                    label="Reference ID"
                    autoFocus
                    fullWidth
                  />
                )}
              />

              <Controller
                name="source"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value || ''}
                    onChange={onChange}
                    error={!!errors.source}
                    helperText={errors.source?.message as string}
                    label="Source"
                    autoFocus
                    fullWidth
                  />
                )}
              />

              <FixedOptionSelector name="source_type" label="Source Type" control={control} options={SOURCE_TYPE} />
              <FixedOptionSelector name="severity" label="Severity" control={control} options={SEVERITY} />
              <FixedOptionSelector name="stage" label="Stage" control={control} options={STAGE} />
              <FormControlLabel
                label="Active"
                control={
                  <Controller
                    name="is_active"
                    control={control}
                    render={({ field }) => (
                      <Checkbox onChange={e => field.onChange(e.target.checked)} checked={!!field.value} />
                    )}
                  />
                }
              />
              <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <TaggedItemsEditor
                    objectId={unifiedMitigationId}
                    contentType="unified_mitigation"
                    disabled={formLoading}
                  />
                )}
              />
            </Box>
          </Edit>
        </form>
        <HistoryDrawer drawer={drawer} selectedId={unifiedMitigationId} model="UnifiedMitigation" />
      </Box>
    </FormProvider>
  );
};

export default UnifiedMitigationsEdit;
